import type { IntegrationConfig, IntegrationDescriptor, IntegrationImportCompleted,
  IntegrationImportFailed } from "@kaleidoscope-solutions/engine-wasm"
import type { ModelFacade } from "@/utils/useEngineModel/ModelFacade"
import {
  IntegrationProvider,
  IntegrationTarget,
  MessageKind
} from "@kaleidoscope-solutions/engine-wasm"
import { defineStore } from "pinia"

import messageHandler from "@/logic/message-handler"
import { integrationTargetLabel } from "@/utils/integrationLabels"
import { captureError } from "@/utils/useEngineModel/ModelFacade"
import { useSnackbar } from "@/utils/useSnackbar"

const { addSnackbar } = useSnackbar()

// Normalise incoming integration target which may arrive as a string name, numeric enum value
// or even a stringified number. Always return the numeric enum member (or original if already correct).
function normalizeIntegrationTarget(raw: number | string): IntegrationTarget {
  if (typeof raw === "string") {
    // Direct key lookup (e.g. "ShopifySales")
    const byKey = (IntegrationTarget)[raw as keyof typeof IntegrationTarget]
    if (byKey !== undefined) {
      return byKey
    }
    // Attempt to parse numeric string
    const maybeNum = Number(raw)
    if (!Number.isNaN(maybeNum) && IntegrationTarget[maybeNum] !== undefined) {
      return maybeNum
    }
  }

  // If already a number assume it's a valid enum value.
  return raw as IntegrationTarget
}

export enum Integration {
  Shopify = "Shopify",
}

export enum IntegrationRunStatus {
  Running = "running",
  Failed = "failed",
  Success = "success",
  Idle = "idle",
}

interface State {
  model?: ModelFacade
  dialog: boolean
  integrations: IntegrationConfig[]
  integrationDescriptors: IntegrationDescriptor[]
  currentIntegration?: Integration
  integrationStatus: IntegrationRunStatus
  importTargets: Record<IntegrationTarget, boolean>
  // Unified per-target status tracking
  targetStatuses: { target: IntegrationTarget; status: IntegrationRunStatus }[]
  // Flags controlling initial auto-run behaviour
  integrationsLoaded: boolean
  hadConfiguredIntegrationsOnInitialLoad?: boolean
  didInitialAutoRun: boolean
  manualRunInProgress: boolean
}

export const useIntegrationStore = defineStore("integration", {
  state: (): State => ({
    model: undefined,
    dialog: false,
    integrations: [],
    integrationDescriptors: [],
    currentIntegration: undefined,
    integrationStatus: IntegrationRunStatus.Idle,
    targetStatuses: [],
    importTargets: {
      [IntegrationTarget.ShopifySales]: true,
      [IntegrationTarget.ShopifyStock]: true,
      [IntegrationTarget.ShopifyGoodsInTransit]: true,
    },
    integrationsLoaded: false,
    hadConfiguredIntegrationsOnInitialLoad: undefined,
    didInitialAutoRun: false,
    manualRunInProgress: false,
  }),
  actions: {
    async initialise(model: ModelFacade) {
      this.model = model
      this.setupListeners()
      this.modelApi.integrations()
      await this.getIntegrationTargets()
      this.attemptInitialAutoRun()
    },
    setupListeners() {
      messageHandler.on(MessageKind.AppOffline, () => {
        console.error("App Offline")

        this.targetStatuses.filter((status) => status.status === IntegrationRunStatus.Running).forEach((target) => {
          // Handle each running integration as failed
          this.completeTarget(target.target, false, 0)
        })
      })

      // Updating
      messageHandler.on(MessageKind.IntegrationUpdated, async () => {
        await this.modelApi.integrations()
        await this.getIntegrationTargets()
      })

      messageHandler.on(
        MessageKind.IntegrationUpdateFailed,
        (integration: string) => {
          console.error("IntegrationUpdateFailed", integration)
        }
      )

      // Removing
      messageHandler.on(MessageKind.IntegrationRemoved, async () => {
        addSnackbar({
          type: "success",
          message: "Your integration has been removed",
        })
        // Why do we call .integrations() after removing an integration? I think it is to update the last run date that is shown?
        // It triggers `messageHandler.on(MessageKind.IntegrationsList, (integrations) => {` updates this.integrations
        await this.modelApi.integrations()
        this.getIntegrationTargets()
      })

      // Getting
      messageHandler.on(MessageKind.IntegrationsList, async (integrations) => {
        this.integrations = JSON.parse(integrations) as IntegrationConfig[]
        // Record whether there were configured integrations on the FIRST load only
        if (!this.integrationsLoaded) {
          this.hadConfiguredIntegrationsOnInitialLoad = this.integrations.some((i) => i.token_status === "TokenPresent")
          this.integrationsLoaded = true
          this.attemptInitialAutoRun()
        }
        await this.getIntegrationTargets()
      })

      // Running
      messageHandler.on(
        MessageKind.IntegrationRunCompleted,
        async (jsonData) => {
          // Mark integration run phase as completed (import phase may still set final status)
          await this.runImport(jsonData, this.manualRunInProgress === false)
          await this.modelApi.integrations()
          await this.getIntegrationTargets()
        }
      )

      messageHandler.on(
        MessageKind.IntegrationImportCompleted,
        (message: string) => {
          const integration = JSON.parse(message) as IntegrationImportCompleted
          const target = normalizeIntegrationTarget(integration.integrationTarget)

          this.completeTarget(target, true, integration.importedCount)
        }
      )

      messageHandler.on(MessageKind.IntegrationImportFailed, (message: string) => {
        const integration = JSON.parse(message) as IntegrationImportFailed
        const target = normalizeIntegrationTarget(integration.integrationTarget)

        this.completeTarget(target, false, 0)
      })

      messageHandler.on(MessageKind.IntegrationRunFailed, (integration) => {
        const target = normalizeIntegrationTarget(integration)
        this.completeTarget(target, false, 0)
      })
    },
    closeDialog() {
      this.dialog = false
    },
    reset() {
      this.integrationStatus = IntegrationRunStatus.Idle
      this.targetStatuses = []
      this.manualRunInProgress = false
    },
    setIntegrationStatus(status: IntegrationRunStatus) {
      this.integrationStatus = status
      this.manualRunInProgress = this.manualRunInProgress && status === IntegrationRunStatus.Running
    },
    startTarget(target: IntegrationTarget) {
      target = normalizeIntegrationTarget(target)
      const existing = this.targetStatuses.find((t) => t.target === target)
      if (existing) {
        existing.status = IntegrationRunStatus.Running
      } else {
        this.targetStatuses.push({ target, status: IntegrationRunStatus.Running })
      }

      this.setIntegrationStatus(IntegrationRunStatus.Running)
    },
    completeTarget(target: IntegrationTarget, success: boolean, importedCount: number) {
      target = normalizeIntegrationTarget(target)
      const existing = this.targetStatuses.find((t) => t.target === target)
      if (existing) {
        existing.status = success ? IntegrationRunStatus.Success : IntegrationRunStatus.Failed
      } else {
        this.targetStatuses.push({ target, status: success ? IntegrationRunStatus.Success : IntegrationRunStatus.Failed })
      }
      const silent = !this.manualRunInProgress

      if (success === false) {
        // Ignore silent flag for errors - always show
        addSnackbar({
          type: "error",
          message: integrationTargetLabel(target, IntegrationRunStatus.Failed),
          actions: [
            {
              label: "Retry",
              action: () => {
                this.runIntegration(target)
              }
            },
            {
              label: "Contact support",
              action: () => {
                window.Beacon("open")
              }
            }
          ]
        })
      } else if (!silent) {
        // If this was an initial auto-run we suppress all notifications
        if (importedCount === 0) {
          addSnackbar({
            type: "info",
            message: integrationTargetLabel(target, "noData"),
          })
        } else {
          addSnackbar({
            type: "success",
            message: integrationTargetLabel(target, IntegrationRunStatus.Success),
          })
        }
      }

      if (this.targetStatuses.every((t) => t.status === IntegrationRunStatus.Success)) {
        this.setIntegrationStatus(IntegrationRunStatus.Success)
      }
      if (this.targetStatuses.some((t) => t.status === IntegrationRunStatus.Failed)) {
        this.setIntegrationStatus(IntegrationRunStatus.Failed)
      }
    },
    setCurrentIntegration(integration: Integration) {
      this.currentIntegration = integration
      this.getIntegrationTargets()
    },
    isConfigured(integration: Integration) {
      const configuration = this.integrations.find((i) => i.integration === integration)

      if (configuration === undefined) {
        return false
      }

      return configuration.token_status === "TokenPresent"
    },
    integrationConfig(integration: Integration) {
      return this.integrations.find((i) => i.integration === integration)
    },
    async updateIntegration(config: IntegrationConfig.Shopify) {
      await this.modelApi.updateIntegration(config)
    },
    async removeIntegration(integration: Integration) {
      await this.modelApi.removeIntegration(IntegrationProvider[integration])
      await this.getIntegrationTargets()
      this.reset()
    },
    async removeAllIntegrations() {
      for (let i = 0; i < this.integrations.length; i++) {
        const integration = this.integrations[i]
        await this.modelApi.removeIntegration(
          IntegrationProvider[integration.integration]
        )
      }
    },
    async runIntegration(integration: IntegrationTarget) {
      const target = normalizeIntegrationTarget(integration)
      this.startTarget(target)
      await this.modelApi.runIntegration(target)
    },

    async runCurrentIntegration(manual: boolean) {
      if (this.integrationDescriptors === undefined) {
        throw new Error("Current integration not set.")
      }

      this.manualRunInProgress = manual
      const silent = !manual

      for (const integration of this.integrationDescriptors) {
        if (!silent) {
          addSnackbar({
            type: "warning",
            message: integrationTargetLabel(integration.name, IntegrationRunStatus.Running),
            refOrGetter: () => this.targetStatuses.find((t) => t.target === integration.name)?.status,
            timeout: 999999,
            condition: (v) => v !== IntegrationRunStatus.Running,
          })
        }

        await this.runIntegration(integration.name)
      }
    },

    attemptInitialAutoRun() {
      // Guard: only run once, and only if there were configured integrations at initial load
      if (this.didInitialAutoRun) {
        return
      }
      if (!this.integrationsLoaded) {
        return
      }
      if (!this.hadConfiguredIntegrationsOnInitialLoad) {
        return
      }
      this.runCurrentIntegration(false)
      this.didInitialAutoRun = true
    },

    async runImport(jsonData: string, silent: boolean = true) {
      try {
        const data = this.model!.parseResult<Record<keyof typeof IntegrationTarget, unknown[]>>(jsonData)

        let target: IntegrationTarget
        let dataLength: number
        if (data?.ShopifySales !== undefined) {
          target = IntegrationTarget.ShopifySales
          dataLength = data.ShopifySales.length
        } else if (data?.ShopifyStock !== undefined) {
          target = IntegrationTarget.ShopifyStock
          dataLength = data.ShopifyStock.length
        } else if (data?.ShopifyGoodsInTransit !== undefined) {
          target = IntegrationTarget.ShopifyGoodsInTransit
          dataLength = data.ShopifyGoodsInTransit.length
        } else {
          throw new Error("Unexpected target")
        }

        if (dataLength === 0) {
          // TODO(Max) would it be better for this to happen automatically engine side, rather than rely on the UI to ensure last_ran is updated?
          // Also ideally we would not create a backup in the case that only last_ran has been updated in the model?
          await this.model?.save(async () => {
            await this.modelApi.addOrUpdateIntegrationLastRan(target)
          })

          this.completeTarget(target, true, 0)

          return
        }

        const importData = JSON.stringify({
          dataFormat: "shopify_json",
          data,
        })

        await this.model?.save(async () => {
          await this.modelApi.import(importData)
        })
      } catch (error) {
        captureError(error, { snackbar: !silent })
      }
    },
    async updateIntegrationTargets(newIntegrationTargets: IntegrationDescriptor[]) {
      await Promise.all(
        newIntegrationTargets.map((descriptor) =>
          this.modelApi.addOrUpdateIntegrationDescriptor(descriptor)
        )
      )
    },
    async removeIntegrationTarget(integrationTarget: IntegrationTarget) {
      await this.modelApi.removeIntegrationTarget(integrationTarget)
    },
    async deleteIntegrationData(integration: IntegrationTarget) {
      await this.modelApi.deleteIntegrationData(integration)
    },
    async getIntegrationTargets() {
      this.integrationDescriptors = await this.modelApi.integrationDescriptors()

      if (this.isConfigured(Integration.Shopify)) {
        this.importTargets = {
          [IntegrationTarget.ShopifySales]: this.getIntegrationByTarget(IntegrationTarget.ShopifySales) !== undefined,
          [IntegrationTarget.ShopifyStock]: this.getIntegrationByTarget(IntegrationTarget.ShopifyStock) !== undefined,
          [IntegrationTarget.ShopifyGoodsInTransit]: this.getIntegrationByTarget(IntegrationTarget.ShopifyGoodsInTransit) !== undefined,
        }
      } else {
        // Reset import targets if Shopify is not configured
        this.importTargets = {
          [IntegrationTarget.ShopifySales]: true,
          [IntegrationTarget.ShopifyStock]: true,
          [IntegrationTarget.ShopifyGoodsInTransit]: true,
        }
      }
    },
    getIntegrationByTarget(target: IntegrationTarget): IntegrationDescriptor | undefined {
      return this.integrationDescriptors.find((i) => `${i.name}` === `${target}`)
    }
  },
  getters: {
    modelApi(state) {
      if (state.model === undefined) {
        throw new Error("Model not initialised")
      }
      return state.model?.api
    },
    currentIntegrationConfig(state): IntegrationConfig | undefined {
      return state.integrations.find(
        (i) => i.integration === state.currentIntegration
      )
    },

  },
})
